import { useJsApiLoader } from "@react-google-maps/api";

const libraries: ("places")[] = ["places"];

export interface IShippingConfig {
  minDistance: number;
  minTotal: number;
  basePrice: number;
  priceByMinute: number;
  priceByKm: number;
}

export interface ILatLng {
  lat: number;
  lng: number;
}

export interface IShippingResult {
  /** Precio final para esta orden (con el descuento de envío gratis ya aplicado, si corresponde). */
  price: number;
  /** Precio base por distancia, SIN el descuento de envío gratis. Es lo único que tiene sentido cachear
   *  por (dirección, sucursal): el descuento depende del total de cada orden, no de la dirección. */
  rawPrice: number;
  distanceMeters: number;
  durationSeconds: number;
  durationText: string;
  destinationAddress: string;
}

/**
 * Calcula el descuento de "envío gratis" (compra > minTotal) a partir de un
 * precio base ya calculado (cacheado o recién obtenido) y la distancia. Pura,
 * no requiere Google Maps: se usa para aplicar el descuento con el total de
 * la orden ACTUAL a un precio base cacheado de una orden anterior distinta.
 */
export const applyFreeShippingDiscount = (
  rawPrice: number,
  distanceMeters: number,
  config: IShippingConfig,
  orderTotal: number
): number => {
  const isFreeShipping = orderTotal > config.minTotal;
  if (!isFreeShipping) return rawPrice;
  if (distanceMeters <= config.minDistance) return 0;
  return rawPrice - config.basePrice;
};

/**
 * Envuelve google.maps.DistanceMatrixService en una Promise y aplica la
 * misma fórmula de precio usada históricamente en Map.tsx (con envío
 * gratis si orderTotal supera minTotal) y en shippingQuote.tsx (sin
 * orderTotal, solo cotización).
 */
export const useShippingCalculator = () => {
  // Mismo id que Map.tsx/shippingQuote.tsx: @react-google-maps/api comparte
  // la carga del script entre todos los usos de este mismo id, así que esto
  // no duplica el <script>, solo garantiza que "google" ya exista aunque
  // <Map> no esté montado (ej. al mostrar la lista de direcciones guardadas).
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_KEY ?? "",
    libraries,
  });

  const calculateShipping = (
    origin: ILatLng,
    destination: ILatLng,
    config: IShippingConfig,
    orderTotal?: number
  ): Promise<IShippingResult> => {
    return new Promise((resolve, reject) => {
      if (!isLoaded || typeof google === "undefined") {
        reject(new Error("Google Maps aún no está listo"));
        return;
      }
      const service = new google.maps.DistanceMatrixService();
      const originLatLng = new google.maps.LatLng(origin.lat, origin.lng);
      const destinationLatLng = new google.maps.LatLng(destination.lat, destination.lng);

      service.getDistanceMatrix(
        {
          origins: [originLatLng],
          destinations: [destinationLatLng],
          travelMode: google.maps.TravelMode.DRIVING,
          unitSystem: google.maps.UnitSystem.METRIC,
        },
        (response, status) => {
          if (status !== "OK" || !response) {
            reject(new Error(`Distance Matrix error: ${status}`));
            return;
          }

          const element = response.rows[0].elements[0];
          const distanceMeters = element.distance.value;
          const durationSeconds = element.duration.value;
          const minutes = durationSeconds / 60;
          const { minDistance, basePrice, priceByKm, priceByMinute } = config;

          let rawPrice: number;
          if (distanceMeters <= minDistance) {
            rawPrice = basePrice;
          } else {
            // Km cobrados = lo que exceda minDistance (los primeros minDistance
            // metros van incluidos en basePrice), no un 1km fijo.
            const extraKm = Number(((distanceMeters - minDistance) / 1000).toFixed(0));
            rawPrice = basePrice + priceByKm * extraKm + priceByMinute * minutes;
          }

          const price =
            typeof orderTotal === "number"
              ? applyFreeShippingDiscount(rawPrice, distanceMeters, config, orderTotal)
              : rawPrice;

          resolve({
            price,
            rawPrice,
            distanceMeters,
            durationSeconds,
            durationText: element.duration.text,
            destinationAddress: response.destinationAddresses?.[0] ?? "",
          });
        }
      );
    });
  };

  return { calculateShipping, isLoaded };
};
